/**
 * QuarantinePanel -- what /o/:orgId/overview shows when the selected org is the quarantine
 * holding area (2026-10-19, OP#1932). Replaces the customer-style "Every site for ..." table there.
 *
 * WHY. The holding area (org 1, stored as "Default Organization") is not a customer: devices
 * land in it when they connect before anyone has assigned them. The customer Overview presented
 * it as "Every site for Default Organization -- 1 site · 0 devices", which answered none of the
 * operator's questions. This panel answers the right ones:
 *   1. how many devices are waiting, and which (serial, model, first seen, last contact);
 *   2. what the page is for (QUARANTINE_EXPLAINER, one line);
 *   3. what to do: Assign the device to a customer site (POST /devices/assign, the existing
 *      claim path). Only platform_admin may release a device -- the server skips a pool device
 *      for anyone else, so below platform_admin the action is replaced by the reason, never
 *      offered as a dead click. Assigning can queue a firmware upgrade (customer policy); the
 *      note says so up front and the result line reports it.
 *
 * Placeholder sites in the holding area are excluded from every count and named in a footnote
 * (lib/quarantine.placeholderSitesNote). Tokens throughout, so light and dark themes follow.
 */
import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import type { ColumnDef } from "@tanstack/react-table";
import { useAssignDevices, useQuarantineDevices } from "@/lib/api/hooks";
import { useSession } from "@/stores/session";
import { DataGrid } from "@/components/data/DataGrid";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { fmtAbsolute, relTime } from "@/lib/format";
import {
  QUARANTINE_EMPTY,
  QUARANTINE_EXPLAINER,
  QUARANTINE_FIRMWARE_NOTE,
  QUARANTINE_LABEL,
  QUARANTINE_ROLE_NOTE,
  assignResultMessage,
  assignTargets,
  placeholderSitesNote,
  sortWaiting,
  waitingSummary,
} from "@/lib/quarantine";
import type { Device } from "@/types/domain";

export function QuarantinePanel() {
  const orgId = useSession((s) => s.currentOrgId);
  const org = useSession((s) => s.currentOrg());
  const me = useSession((s) => s.me);
  // The release gate mirrors the server: platform_admin only (devices.py assign_devices_to_site).
  const canRelease = me?.is_platform_admin === true;
  const { data, isLoading, error } = useQuarantineDevices(orgId);

  const devices = useMemo(() => sortWaiting(data?.items ?? []), [data]);
  // The server's total, not the page length: the count must not cap at the fetched page.
  const waiting = data?.total ?? devices.length;
  const placeholders = useMemo(
    () => (me ? me.sites.filter((s) => s.org_id === orgId) : []),
    [me, orgId],
  );
  const targets = useMemo(() => (me ? assignTargets(me.orgs, me.sites) : []), [me]);

  const [assigning, setAssigning] = useState<Device | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const columns = useMemo<ColumnDef<Device, unknown>[]>(
    () => [
      {
        id: "serial",
        header: "Serial",
        accessorFn: (d) => d.serial,
        cell: (ctx) => <span className="font-mono text-ink">{ctx.row.original.serial}</span>,
      },
      {
        id: "model",
        header: "Model",
        accessorFn: (d) => d.model ?? "",
        cell: (ctx) => <span className="text-ink">{ctx.row.original.model || "—"}</span>,
      },
      {
        id: "first_seen",
        header: "First seen",
        accessorFn: (d) => d.registered_at ?? "",
        cell: (ctx) => <TimeCell iso={ctx.row.original.registered_at} />,
      },
      {
        id: "last_contact",
        header: "Last contact",
        accessorFn: (d) => d.last_seen ?? "",
        cell: (ctx) => <TimeCell iso={ctx.row.original.last_seen} />,
      },
      {
        id: "action",
        header: "",
        enableSorting: false,
        cell: (ctx) => (
          <AssignCell device={ctx.row.original} canRelease={canRelease} onAssign={setAssigning} />
        ),
      },
    ],
    [canRelease],
  );

  const footnote = placeholderSitesNote(placeholders);

  return (
    <div data-testid="quarantine-panel">
      <PageHeader
        title={QUARANTINE_LABEL}
        // The stored org name stays visible as SECONDARY text: it is what the audit log, the DB
        // and older screenshots call this org, so an operator can match them up.
        subtitle={org ? `Holding area (stored as “${org.name}”)` : "Holding area"}
      />

      <p className="text-sm text-ink" data-testid="quarantine-explainer">
        {QUARANTINE_EXPLAINER}
      </p>

      {error && (
        <div className="mt-3">
          <ErrorBanner error={error} />
        </div>
      )}

      {data && (
        <p role="status" className="mt-3 text-sm font-medium text-ink" data-testid="quarantine-count">
          <span className="tabular-nums">{waitingSummary(waiting)}</span>
        </p>
      )}

      <div className="mt-2 text-xs text-ink-muted" data-testid="quarantine-release-note">
        {canRelease ? QUARANTINE_FIRMWARE_NOTE : QUARANTINE_ROLE_NOTE}
      </div>

      {result && (
        <p role="status" className="mt-2 text-sm text-ink" data-testid="quarantine-result">
          {result}
        </p>
      )}

      <div className="mt-3">
        {data && devices.length === 0 ? (
          <EmptyState
            title={QUARANTINE_EMPTY}
            hint="A device that connects before it is assigned to a customer will appear here."
          />
        ) : (
          <DataGrid
            data={devices}
            columns={columns}
            rowKey={(d) => d.serial}
            dense
            isLoading={isLoading}
            pageSize={50}
            initialSorting={[]}
            empty={<span>{QUARANTINE_EMPTY}</span>}
            renderCard={(d) => (
              <WaitingCard device={d} canRelease={canRelease} onAssign={setAssigning} />
            )}
          />
        )}
      </div>

      {footnote && (
        <p className="mt-3 text-xs text-ink-subtle" data-testid="quarantine-placeholder-note">
          {footnote}
        </p>
      )}

      {canRelease && (
        <AssignDialog
          device={assigning}
          targets={targets}
          onClose={() => setAssigning(null)}
          onDone={(msg) => {
            setResult(msg);
            setAssigning(null);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Cells
 * ------------------------------------------------------------------ */

/** Relative time with the absolute time on hover; "never" when the device never reported. */
function TimeCell({ iso }: { iso: string | null | undefined }) {
  return (
    <span className="tabular-nums text-ink" title={iso ? fmtAbsolute(iso) : undefined}>
      {relTime(iso)}
    </span>
  );
}

function AssignCell({
  device,
  canRelease,
  onAssign,
}: {
  device: Device;
  canRelease: boolean;
  onAssign: (d: Device) => void;
}) {
  // Below platform_admin there is no action at all (the role note above the list says why).
  if (!canRelease) return null;
  return (
    <Button
      variant="secondary"
      onClick={(e) => {
        // The row itself is not a link; keep a click here from reaching DataGrid handlers.
        e.stopPropagation();
        onAssign(device);
      }}
      aria-label={`Assign ${device.serial} to a site`}
    >
      Assign…
    </Button>
  );
}

/** The sub-`lg` card DataGrid reflows to. Same facts, stacked. */
function WaitingCard({
  device: d,
  canRelease,
  onAssign,
}: {
  device: Device;
  canRelease: boolean;
  onAssign: (d: Device) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-mono text-ink">{d.serial}</div>
          <div className="truncate text-xs text-ink-muted">{d.model || "—"}</div>
        </div>
        <AssignCell device={d} canRelease={canRelease} onAssign={onAssign} />
      </div>
      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-ink-muted">First seen</dt>
          <dd className="mt-0.5">
            <TimeCell iso={d.registered_at} />
          </dd>
        </div>
        <div>
          <dt className="text-ink-muted">Last contact</dt>
          <dd className="mt-0.5">
            <TimeCell iso={d.last_seen} />
          </dd>
        </div>
      </dl>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Assign dialog (platform_admin only; rendered only when canRelease)
 * ------------------------------------------------------------------ */

function AssignDialog({
  device,
  targets,
  onClose,
  onDone,
}: {
  device: Device | null;
  targets: ReturnType<typeof assignTargets>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const assign = useAssignDevices();
  const [siteId, setSiteId] = useState("");
  const siteName = useMemo(
    () => targets.flatMap((g) => g.sites).find((s) => s.id === siteId)?.name ?? "",
    [targets, siteId],
  );

  const close = () => {
    setSiteId("");
    assign.reset();
    onClose();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!device || !siteId) return;
    assign.mutate(
      // Same body as the Onboard screen; the server re-checks platform_admin for a pool device.
      { serials: [device.serial], site_id: Number(siteId) },
      {
        onSuccess: (r) => {
          setSiteId("");
          onDone(`${device.serial}: ${assignResultMessage(r, siteName)}`);
        },
      },
    );
  };

  return (
    <Dialog.Root open={device !== null} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Assign to a site</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Move <span className="font-mono text-ink">{device?.serial}</span>
            {device?.model ? ` (${device.model})` : ""} out of quarantine and into a customer
            site.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Site</span>
              <select
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                className="min-h-touch rounded border border-line bg-surface px-3 text-ink outline-none focus:border-brand"
                aria-label="Target site"
              >
                <option value="">Choose a site…</option>
                {targets.map((g) => (
                  <optgroup key={g.orgId} label={g.orgName}>
                    {g.sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            {targets.length === 0 && (
              <span className="text-xs text-ink-muted">
                No customer site exists yet. Create a customer and a site first.
              </span>
            )}
            <p className="text-xs text-warn" data-testid="assign-firmware-note">
              {QUARANTINE_FIRMWARE_NOTE}
            </p>
            {assign.isError && (
              <span className="text-xs text-critical">Could not assign the device.</span>
            )}
            <div className="md-dialog-footer flex items-center justify-end gap-2">
              <Button variant="ghost" type="button" onClick={close}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={!siteId || assign.isPending}>
                {assign.isPending ? "Assigning…" : "Assign"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
